import React, { useEffect } from 'react';
import useTranslation from '../hooks/useTranslation';
import { useOperationTime } from '../hooks/useOperationTime';

interface ServiceClosingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ServiceClosingModal: React.FC<ServiceClosingModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { operationStatus } = useOperationTime();

  // 모달이 열릴 때 5초 후 자동으로 닫기
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm mx-4 text-center">
        {/* 아이콘 */}
        <div className="mb-4">
          <svg 
            className="w-16 h-16 mx-auto text-orange-500" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
        </div>

        {/* 제목 */}
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {t('service.closing.title')}
        </h2>

        {/* 30분 전 안내 메시지 */}
        <p className="text-gray-600 text-sm mb-4">
          {t('service.closing.message')}
        </p>

        {/* 서버에서 가져온 운영시간 메시지 (있는 경우) */}
        {operationStatus.operationHours && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
            <p className="text-orange-800 text-sm font-medium text-center">
              📍 {operationStatus.operationHours}
            </p>
          </div>
        )}

        {/* 자동 닫힘 안내 */}
        <p className="text-xs text-gray-400">
          {t('service.closing.autoClose')}
        </p>
      </div>
    </div>
  );
};

export default ServiceClosingModal; 